import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { Button } from './ui/button';

const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "I had no idea TikTok was selling my daughter's location data until SaidSimply™ flagged it. Changed our app permissions that same day.",
      author: "Sarah M.",
      role: "Mother of 2, California",
      avatar: "SM",
    },
    {
      quote: "Finally, someone translated the legal jargon. I actually understand what I'm agreeing to now — and more importantly, what my kids are.",
      author: "David K.",
      role: "Father of 3, Texas",
      avatar: "DK",
    },
    {
      quote: "The policy change alerts alone are worth it. I got notified the day Instagram changed their data terms. Peace of mind I didn't know I needed.",
      author: "Jennifer L.",
      role: "Mother of 1, New York",
      avatar: "JL",
    },
  ];

  const stats = [
    { number: '10,000+', label: 'Families Protected' },
    { number: '500K+', label: 'Policies Monitored' },
    { number: '99.9%', label: 'Uptime' },
    { number: '4.9★', label: 'Parent Rating' },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#FDFBF7]" id="about" data-testid="testimonials-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {stats.map((stat, index) => (
            <div key={stat.label} className="text-center" data-testid={`stat-${index}`}>
              <div className="font-heading text-3xl md:text-4xl font-semibold text-[#0D9488]">
                {stat.number}
              </div>
              <div className="text-sm text-[#78716C] mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Testimonials Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-[#D97706] font-medium text-sm uppercase tracking-wider">Why Families Trust Us</span>
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#292524] mt-4"
            data-testid="testimonials-heading"
          >
            Trusted by 10,000+ Parents
          </h2>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-md transition-shadow duration-300 border border-stone-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`testimonial-${index}`}
            >
              <Quote className="w-10 h-10 text-[#0D9488]/20 mb-4" />
              <p className="text-[#292524] leading-relaxed mb-6">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#0D9488]/10 flex items-center justify-center">
                  <span className="text-[#0D9488] font-medium">{testimonial.avatar}</span>
                </div>
                <div>
                  <div className="font-medium text-[#292524]">{testimonial.author}</div>
                  <div className="text-sm text-[#78716C]">{testimonial.role}</div>
                </div>
              </div>
              <div className="flex gap-1 mt-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Final CTA */}
        <motion.div
          className="mt-20 text-center bg-stone-50 rounded-3xl p-12 md:p-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="final-cta-section"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-[#292524] mb-4">
            Ready to protect your family's digital privacy?
          </h2>
          <p className="text-lg text-[#78716C] mb-8 max-w-xl mx-auto">
            Join thousands of parents who finally understand what they're agreeing to.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-8 py-6 text-lg font-medium shadow-lg shadow-teal-900/10 hover:shadow-teal-900/20 transition-all hover:-translate-y-0.5"
              data-testid="final-cta-primary"
              asChild
            >
              <a href="https://app.trysaidsimply.com/auth">Start Protecting My Family — Free for 14 Days</a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
